import { ChangeDetectorRef, Component, EventEmitter, Input, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ToastService } from 'angular-toastify';
import { Action } from 'src/app/core/models/action.model';
import { GuideService } from 'src/app/modules/client/pages/guide/services/guide.service';
import { ClaimWithCase } from 'src/app/core/services/claims.service';
import { KycService } from 'src/app/core/services/kyc.service';
import { UserService } from 'src/app/core/services/user.service';
import { Kyc_Check } from 'src/app/core/models/kyc_check.model';
import { DocumentKycUploadModalComponent } from 'src/app/shared/components/simple/document-kyc-upload-modal/document-kyc-upload-modal.component';
import { TextractKycProviderComponent } from 'src/app/shared/components/simple/document-kyc-upload-modal/textract-kyc-provider/textract-kyc-provider.component';
import { ThirdfortKycProviderComponent } from 'src/app/shared/components/simple/document-kyc-upload-modal/thirdfort-kyc-provider/thirdfort-kyc-provider.component';
import { VeriffKycProviderComponent } from 'src/app/shared/components/simple/document-kyc-upload-modal/veriff-kyc-provider/veriff-kyc-provider.component';
import { VeriffLiteKycProviderComponent } from 'src/app/shared/components/simple/document-kyc-upload-modal/veriff-lite-kyc-provider/veriff-lite-kyc-provider.component';
import { SomosKycV1ProviderComponent } from 'src/app/shared/components/simple/document-kyc-upload-modal/somos-kyc-v1-provider/somos-kyc-v1-provider.component';
import { KYCActionState } from 'src/app/core/models/kyc-validation.model';
import { KycStateService } from 'src/app/core/services/kyc-state.service';
import {
  LegalNameMismatchResolver,
  UniqueDocumentValueResolver,
} from 'src/app/core/services/kyc-resolvers';
import { AgentClientsService } from 'src/app/core/services/agent-clients.service';
import { DeferCaseActionService } from 'src/app/core/services/defer-case-action.service';
import { buildPrereqTooltip } from 'src/app/core/utils/prereq-tooltip.util';

@Component({
  selector: 'app-oa-kyc',
  templateUrl: './oa-kyc.component.html',
  styleUrls: ['./oa-kyc.component.scss', '../oa-styles.scss'],
})
export class OaKycComponent {
  @Input() action!: Action;
  @Input() userId?: string;
  @Input() isAgent: boolean = false;
  @Input() claimSelected?: ClaimWithCase | null;
  @Output() changed = new EventEmitter<boolean>();
  @Output() openKycModal = new EventEmitter<void>();

  state: KYCActionState = 'LOADING';
  currentKycCheck?: Kyc_Check;

  deferring = false;
  deferred = false;

  constructor(
    public dialog: MatDialog,
    private us: UserService,
    private ks: KycService,
    private kycStateService: KycStateService,
    private ts: ToastService,
    private cdr: ChangeDetectorRef,
    private translate: TranslateService,
    private guideService: GuideService,
    private router: Router,
    private agentClientsService: AgentClientsService,
    private deferCaseActionService: DeferCaseActionService
  ) {
    // Registrar resolvers específicos para este componente
    this.registerResolvers();
  }

  /**
   * Registra os resolvers de validação para este componente
   * Outros componentes podem registrar resolvers diferentes
   */
  private registerResolvers(): void {
    this.kycStateService.registerResolver(
      new LegalNameMismatchResolver(this.dialog, this.ks)
    );
    this.kycStateService.registerResolver(new UniqueDocumentValueResolver());
  }

  async ngOnInit() {
    console.log('[OA] [KYC] init', this.action);

    const deferredClaims = this.action.case_action_config?.simple_questionnaire?.deferred_claims;
    this.deferred = Array.isArray(deferredClaims) && !!this.action.claim_id && deferredClaims.includes(this.action.claim_id);

    // Verificar se a claim está inativa (apenas para agentes)
    if (this.isAgent && this.claimSelected?.status === 'INACTIVE') {
      this.state = 'BLOCKED';
      console.log('[OA] [KYC] Blocked - claim is inactive');
      this.cdr.detectChanges();
      return;
    }

    // CPK-4442: KYC FAILED mas adiado (postpone) — no card volta a ser uma ação
    // pendente comum (linha clicável → startKycProcess → modal do provider, que
    // já renderiza a UX de postpone pelos notes), em vez do check verde que o
    // checkState devolveria (DONE_OK via canProceed). Só exibição: o gating de
    // pré-requisitos segue intacto (ac_status='DONE', CPK-3620).
    if (this.action.postponed) {
      this.state = 'PENDING';
      console.log('[OA] [KYC] Postponed — rendering as PENDING (CPK-4442)');
      this.cdr.detectChanges();
      return;
    }

    this.state = await this.kycStateService.checkState(this.action);
    console.log('[OA] [KYC] state', this.state);
    this.cdr.detectChanges();
  }

  /**
   * Método principal para iniciar KYC
   * Agora usa o KycStateService para verificar e resolver issues internas
   */
  async startKycProcess() {
    // Verificar se a claim está inativa (apenas para agentes)
    if (this.isAgent && this.claimSelected?.status === 'INACTIVE') {
      this.ts.error('Cannot start KYC process for inactive claim');
      return;
    }

    // ✅ CPK-3911: se o claim desta action ainda exige o guide (wizard
    // habilitado e com actions pendentes), redireciona para lá em vez de
    // abrir o modal de KYC diretamente a partir do card fora do guide.
    if (this.action.claim_id) {
      const requiresGuide = await this.guideService.claimRequiresGuide(
        this.action.claim_id
      );

      if (requiresGuide) {
        console.log('[OA] [KYC] Claim still requires guide, redirecting', {
          claimId: this.action.claim_id,
        });
        this.router.navigateByUrl(`client/claim/${this.action.claim_id}/guide`);
        return;
      }
    }

    const provider = this.action.config?.provider;

    // Se é Veriff, VeriffLite ou o clone SomosKycV1 e está DONE_FAIL, tenta resolver issues internas
    if (
      (provider === 'veriff' || provider === 'verifflite' || provider === 'somoskyc_v1') &&
      this.state === 'DONE_FAIL'
    ) {
      console.log('[OA] [KYC] Attempting to resolve internal issues...');

      const result = await this.kycStateService.checkAndResolveInternalIssues(
        this.action,
        { dialog: this.dialog, userId: this.userId } // Passa o MatDialog e userId para os resolvers
      );

      console.log('[OA] [KYC] Resolution result:', result);

      // Se resolveu tudo e o novo estado é DONE_OK, apenas atualiza UI
      if (result.allResolved && result.newState === 'DONE_OK') {
        this.state = result.newState;
        this.changed.emit(true);
        return;
      }

      // Se resolveu parcialmente mas ainda precisa de novo KYC
      if (result.someResolved && result.requiresNewKyc) {
        console.log('[OA] [KYC] Some issues resolved, but new KYC required');
        // Continua para abrir modal de KYC
      }

      // Se não resolveu nada, continua para refazer KYC
      if (!result.someResolved && result.requiresNewKyc) {
        console.log('[OA] [KYC] Could not resolve issues, new KYC required');
        // Continua para abrir modal de KYC
      }
    }

    // Fluxo normal - abre modal para fazer/refazer KYC
    await this.uploadDialog();
  }

  async uploadDialog() {
    const uid = this.userId || (await this.us.getCurrentUser())?.id;

    if (!this.action) {
      throw new Error('No requirement loaded');
    }

    if (!uid) {
      throw new Error('No user logged in');
    }

    // Re-check live state before opening the dialog: an agent may have added a manual pass
    // after the action list was last loaded. If so, skip the dialog entirely.
    const passedCheck = await this.ks.getPassedCheckForUser(uid, this.action.action_id || undefined);
    if (passedCheck) {
      this.state = 'DONE_OK';
      this.changed.emit(true);
      return;
    }

    console.log('ACTION', this.action);
    
    // Busca notes e provider_return se houver done_action_id (KYC falhou)
    let notes: any[] | undefined;
    let providerReturn: any | undefined;
    if (this.action.done_action_id) {
      try {
        const kycCheck = await this.ks.getById(this.action.done_action_id);
        notes = kycCheck?.notes;
        providerReturn = kycCheck?.provider_return;
        console.log('[OA] [KYC] Retrieved data for previous KYC', {
          actionId: this.action.action_id,
          hasNotes: !!notes,
          notesCount: notes?.length || 0,
          hasProviderReturn: !!providerReturn,
        });
      } catch (error) {
        console.error('[OA] [KYC] Error fetching KYC check data:', error);
      }
    }
    
    const dialogData: {
      bucket: string;
      name: string;
      type: 'PERSONAL' | 'CLAIM';
      multiple: boolean;
      ref: string;
      provider: string;
      action_id?: string;
      claim_id?: string;
      notes?: any[];
      provider_return?: any;
      reopenedFromOutstandingAction?: boolean;
    } = {
      bucket: 'client-documents',
      name: 'kyc.png',
      multiple: false,
      ref: uid,
      type: 'PERSONAL',
      provider: this.action.config.provider,
      action_id: this.action.action_id || undefined,
      claim_id: this.action.claim_id || undefined,
      notes, // Passa notes para detecção de erros
      provider_return: providerReturn, // Passa provider_return para detecção de erros
      // CPK-4807: postpone reaberto pelo card — a tela de postpone oferece retry (ou "Fechar"
      // sem retries) em vez do "Prosseguir" do guide, que aqui só fecharia o modal.
      reopenedFromOutstandingAction: !!this.action.postponed,
    };

    let provider;
    console.log('PROVIDER', this.action.config.provider);
    switch (this.action.config.provider) {
      case 'textract':
        provider = TextractKycProviderComponent;
        break;
      case 'thirdfort':
        provider = VeriffLiteKycProviderComponent;
        break;
      case 'veriff':
        provider = VeriffKycProviderComponent;
        break;
      case 'verifflite':
        provider = VeriffLiteKycProviderComponent;
        break;
      case 'somoskyc_v1':
        provider = SomosKycV1ProviderComponent;
        break;
      // TODO: Add more providers here
      default:
        provider = DocumentKycUploadModalComponent;
        break;
    }
    console.log('PROVIDER', provider.name);

    const dialogRef = this.dialog.open(provider as any, {
      data: dialogData,
      height: '90vh',
      minHeight: '500px',
      maxHeight: '700px',
      width: '90vw',
      maxWidth: '700px',
      // Do NOT force `disableClose` here: only the Somos guided-capture flow wants
      // confirm-to-close, and it sets `dialogRef.disableClose = true` itself in
      // BaseKycProviderComponent.ngOnInit (wiring backdrop/ESC to confirmClose).
      // Every other provider (Veriff / VeriffLite / Textract / Thirdfort) must keep
      // MatDialog's default close (backdrop + ESC + header ✕), which forcing the flag
      // here would break (no ESC handler is wired for them).
    });

    dialogRef.afterClosed().subscribe((result) => {
      console.log(`Dialog result: ${result}`);
      console.log('[OA] [KYC]', this);

      this.changed.emit(true);
    });
  }

  checkDependsOn() {
    return false;
    // this.action.description?.includes('depends on');
  }

  get showDeferButton(): boolean {
    return (
      this.agentClientsService.isSimpleQuestionnaireSession() &&
      // CPK-4864: depois de adiada, a action some do "Continuar depois" e fica
      // só com a tag to-do — o cliente retoma pelo próprio card.
      !this.deferred &&
      this.state !== 'LOADING' &&
      this.state !== 'DONE_OK'
    );
  }

  /** CPK-4864: tooltip de bloqueio com o nome das actions pré-requisito pendentes. */
  get blockedTooltip(): string {
    return buildPrereqTooltip(this.action.blocking_prereq_names, this.translate);
  }

  async continueLater(): Promise<void> {
    if (this.deferring || this.deferred) return;

    const uid = this.userId || (await this.us.getCurrentUser())?.id;
    if (!uid || !this.action.case_action_id || !this.action.claim_id) return;

    this.deferring = true;
    try {
      await this.deferCaseActionService.defer(this.action.case_action_id, uid, this.action.claim_id);
      this.deferred = true;
      console.log('[OA] [KYC] Deferred (CPK-4745)', { caseActionId: this.action.case_action_id });
    } catch (error) {
      console.error('[OA] [KYC] Error deferring action', error);
      this.ts.error(this.translate.instant('DeferCaseActionError'));
    } finally {
      this.deferring = false;
    }
  }
}
